import React, { useEffect, useState } from 'react';
import { Row, Col, Spin, Button, Card, Typography, theme } from 'antd';
import { message } from '../../services/toastMessage';
import { PlusOutlined, EditOutlined, CalendarOutlined, StopOutlined, CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { IconBriefcase } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/Layouts/Layout';
import EmptyState from '../../components/EmptyState';
import PageHeaderRow from '../../components/Layouts/PageHeaderRow';
import ConfirmBottomSheet from '../../components/ConfirmBottomSheet';
import { getMyServices, updateService, deleteService, NO_SERVICE_PROVIDER_PROFILE_ERROR } from '../../services/ServiceService';
import { IService } from '../../interfaces/heavyDutyMachinery/IService';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { pillToneStyle } from '../../theme/creamyTokens';

const { Text, Title } = Typography;

type LoadState = 'ok' | 'noProfile' | 'notAvailable' | 'error';

const formatMoney = (amount: number, currency: string) => `${amount.toFixed(2)} ${currency}`;

/**
 * A provider's own Service catalog -- Fase 13's equivalent of MyOperators/
 * MyMachineryAccessories' own list-plus-create pattern, needed purely as
 * scaffolding so ServiceForm's edit route (/heavy-duty-machinery/services/:id/edit)
 * is actually reachable by a real user, not just a URL typed by hand.
 * ServiceProviderProfile onboarding itself is a separate, not-yet-built area
 * (see Fase 13's own architecture doc, section 8) -- getMyServices() 404s
 * with "No service provider profile yet" for a caller without one, and this
 * page renders a friendly explanation instead of a raw error in that case,
 * same "never surface the crude backend message" spirit as ServiceForm's
 * own deposit note.
 */
const MyServicesContent: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [services, setServices] = useState<IService[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadState, setLoadState] = useState<LoadState>('ok');
  const [deletingService, setDeletingService] = useState<IService | undefined>(undefined);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = () => {
    setLoading(true);
    getMyServices()
      .then((data) => {
        setServices(data);
        setLoadState('ok');
      })
      .catch((error: Error) => {
        if (error.message === NO_SERVICE_PROVIDER_PROFILE_ERROR) {
          setLoadState('noProfile');
        } else if (error.message.startsWith('HTTP Error: 404')) {
          // Bare 404 with no message body -- FeatureFlagKeys.ServicesEnabled
          // is off (see ServiceController's own doc comment: 404, never 403,
          // so a caller probing routes gets no signal this feature exists).
          setLoadState('notAvailable');
        } else {
          setLoadState('error');
          message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_LOAD_ERROR));
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleActive = async (service: IService) => {
    try {
      const updated = await updateService(service.id, { active: !service.active });
      setServices((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_STATUS_UPDATE_ERROR));
    }
  };

  const handleDeleteService = async () => {
    if (!deletingService) return;
    setDeleteBusy(true);
    try {
      await deleteService(deletingService.id);
      setServices((prev) => prev.filter((s) => s.id !== deletingService.id));
      setDeletingService(undefined);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_STATUS_UPDATE_ERROR));
    } finally {
      setDeleteBusy(false);
    }
  };

  const priceLabel = (service: IService) => {
    const parts: string[] = [];
    if (service.pricingMode === 'Hourly' || service.pricingMode === 'Both') {
      if (service.hourlyRate != null) parts.push(`${formatMoney(service.hourlyRate, service.hostCurrencyCode)}/h`);
    }
    if (service.pricingMode === 'FixedJob' || service.pricingMode === 'Both') {
      if (service.fixedJobPrice != null) parts.push(formatMoney(service.fixedJobPrice, service.hostCurrencyCode));
    }
    return parts.join(' · ');
  };

  return (
    <AppLayout>
      <div style={{ width: '100%', maxWidth: 1100 }}>
        {/* No title: the nav already says "Servicios", and repeating it pushed
            the only real content -- the list -- below the fold. The subtitle
            and the CTA only show once there is a list to caption; on an empty
            page the centred EmptyState is the whole screen, instead of a
            left-aligned header floating above a centred illustration. */}
        {!loading && loadState === 'ok' && services.length > 0 && (
          <PageHeaderRow
            subtitle={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_PAGE_SUBTITLE)}
            actions={
              <Button type="primary" shape="round" icon={<PlusOutlined />} onClick={() => navigate('/heavy-duty-machinery/services/new')}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_CREATE_BUTTON)}
              </Button>
            }
          />
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : loadState === 'noProfile' ? (
          <EmptyState
            icon={<IconBriefcase size={24} />}
            title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_NO_PROVIDER_PROFILE_TITLE)}
            description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_NO_PROVIDER_PROFILE_DESCRIPTION)}
            action={
              <Button type="primary" onClick={() => navigate('/service-provider/onboarding')}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_NO_PROVIDER_PROFILE_BUTTON)}
              </Button>
            }
          />
        ) : loadState === 'notAvailable' ? (
          <EmptyState
            icon={<IconBriefcase size={24} />}
            title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_NOT_AVAILABLE_TITLE)}
            description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_NOT_AVAILABLE_DESCRIPTION)}
          />
        ) : loadState === 'error' ? (
          <EmptyState
            icon={<IconBriefcase size={24} />}
            title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_LOAD_ERROR)}
            description=""
          />
        ) : services.length === 0 ? (
          <EmptyState
            icon={<IconBriefcase size={24} />}
            title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_EMPTY_TITLE)}
            description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_EMPTY_DESCRIPTION)}
            action={
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/heavy-duty-machinery/services/new')}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICES_CREATE_BUTTON)}
              </Button>
            }
          />
        ) : (
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            {services.map((service) => (
              <Col xs={24} sm={12} md={8} key={service.id}>
                <Card bordered style={{ borderRadius: 18, opacity: service.active ? 1 : 0.6 }} styles={{ body: { padding: 16 } }}>
                  <span
                    style={{
                      display: 'inline-block',
                      alignSelf: 'flex-start',
                      marginBottom: 4,
                      padding: '2px 10px',
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                      ...pillToneStyle(service.active ? 'success' : 'default', token),
                    }}
                  >
                    {translate(t, service.active ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_ACTIVE_STATUS : TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_INACTIVE_STATUS)}
                  </span>

                  <Title level={5} style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {service.title}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 13 }}>{service.serviceCategoryName}</Text>

                  <div style={{ marginTop: 8 }}>
                    {priceLabel(service) && <Text strong style={{ fontSize: 15 }}>{priceLabel(service)}</Text>}
                    {service.depositAmount != null && (
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                        {formatMoney(service.depositAmount, service.hostCurrencyCode)} {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_DEPOSIT_AMOUNT_LABEL)}
                      </Text>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <Button
                        shape="round"
                        block
                        style={{ height: 40, border: 'none', ...pillToneStyle('default', token) }}
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/heavy-duty-machinery/services/${service.id}/edit`)}
                      >
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_EDIT_BUTTON)}
                      </Button>
                      <Button
                        shape="round"
                        block
                        style={{ height: 40, border: 'none', ...pillToneStyle('default', token) }}
                        icon={<CalendarOutlined />}
                        onClick={() => navigate(`/heavy-duty-machinery/services/${service.id}/availability`)}
                      >
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_MANAGE_AVAILABILITY_BUTTON)}
                      </Button>
                    </div>
                    <Button
                      shape="round"
                      block
                      style={{ height: 40, border: 'none', ...pillToneStyle(service.active ? 'error' : 'success', token) }}
                      icon={service.active ? <StopOutlined /> : <CheckCircleOutlined />}
                      onClick={() => toggleActive(service)}
                    >
                      {translate(t, service.active ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_DEACTIVATE_BUTTON : TRANSLATIONS.HEAVY_DUTY_MACHINERY_SERVICE_REACTIVATE_BUTTON)}
                    </Button>
                    <Button
                      shape="round"
                      block
                      style={{ height: 40, border: 'none', ...pillToneStyle('error', token) }}
                      icon={<DeleteOutlined />}
                      onClick={() => setDeletingService(service)}
                    >
                      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CARD_DELETE_BUTTON)}
                    </Button>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </div>

      <ConfirmBottomSheet
        open={!!deletingService}
        title={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_TITLE)}
        description={deletingService?.title}
        cancelText={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_KEEP)}
        confirmText={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_DELETE)}
        confirmLoading={deleteBusy}
        onConfirm={handleDeleteService}
        onClose={() => setDeletingService(undefined)}
      />
    </AppLayout>
  );
};

// Split into a wrapper + Content component so that a scoped theme can
// be added here without moving anything: theme.useToken() only sees a
// nested ConfigProvider's tokens from a component mounted UNDER it, not
// from the one rendering the provider. There is nothing scoped here
// today -- coral is the app-wide primary (see theme/appTheme).
const MyServices: React.FC = () => {
  const { isMobile } = useAppBreakpoint();
  return isMobile ? (
    <MyServicesContent />
  ) : (
    <MyServicesContent />
  );
};

export default MyServices;
